import { type FC, type PropsWithChildren, type ReactNode, useMemo } from 'react';

import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { FlightInfo } from '@vakantiesnl/components/src/atoms/FlightInfo';
import { Tag } from '@vakantiesnl/components/src/atoms/Tag';
import { OfferItem } from '@vakantiesnl/components/src/molecules/OfferDetails/OfferItem';
import { OfferSummaryTemplate } from '@vakantiesnl/components/src/molecules/OfferSummary/OfferSummaryTemplate';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useRemainingDaysText } from '@vakantiesnl/components/src/utils/user/useRemainingDaysText';

import { useStyles } from './UpcomingOfferSummary.styles';
import { type OfferSummaryProps } from '../AccoOfferSummary';

export type UpcomingOfferSummaryProps = Omit<OfferSummaryProps, 'party'> & {
	/** Date of departure */
	departureDate: string;
	/** Date of arrival */
	arrivalDate: string;
	/** The booking number */
	bookingNumber: string;
	/** The number of passengers */
	numberOfPassengers: number;
	/** If booking is acco only */
	isAccoOnly: boolean;
};

export const UpcomingOfferSummary: FC<PropsWithChildren<UpcomingOfferSummaryProps>> = ({
	accommodation,
	microCopies,
	mealplanDescription,
	departureDate,
	arrivalDate,
	numberOfPassengers,
	bookingNumber,
	isAccoOnly,
}) => {
	const { classes } = useStyles();
	const { isDesktop } = useBreakpoints();

	const viewBookingLabel = useMemo(() => microCopies['user.viewBooking'], [microCopies]);

	const bookingLink = useMemo(
		() => `/${process.env.NEXT_PUBLIC_USER_BASE_PATH}/boeking/${bookingNumber}`,
		[bookingNumber],
	);

	const remainingDaysText = useRemainingDaysText(departureDate);

	const ImageOverlay = (): ReactNode => (
		<Tag leadingIcon="time" color="teal">
			{remainingDaysText}
		</Tag>
	);

	const TopBlock = (): ReactNode => (
		<>
			<OfferItem
				variant="bold"
				iconType="destination"
				title={`${accommodation.country}, ${accommodation.region},	${accommodation.city}`}
				dataCy="OfferSummary-destination"
			/>
			<OfferItem variant="bold" iconType="hashtag" title={bookingNumber} dataCy="OfferSummary-bookingNumber" />
			<OfferItem
				variant="bold"
				iconType="familyRoom"
				title={`${numberOfPassengers} ${microCopies['filters.persons' + (numberOfPassengers === 1 ? 'Singular' : 'Plural')].toLowerCase()}`}
				dataCy="OfferSummary-pax"
			/>
			{mealplanDescription && (
				<OfferItem variant="bold" iconType="food" title={mealplanDescription} dataCy="OfferSummary-mealplan" />
			)}
		</>
	);

	const MidBlock = (): ReactNode => (
		<>
			<FlightInfo
				flightDate={departureDate}
				isOutbound={true}
				className={classes.flight}
				dataCy="OfferSummary-outboundFlight"
				showFlightDetails={false}
				customLabel={isAccoOnly ? microCopies['user.booking.checkin'] : undefined}
			/>
			<FlightInfo
				flightDate={arrivalDate}
				isOutbound={false}
				className={classes.flight}
				dataCy="OfferSummary-inboundFlight"
				showFlightDetails={false}
				customLabel={isAccoOnly ? microCopies['user.booking.checkout'] : undefined}
			/>
		</>
	);

	const BottomBlock = (): ReactNode => (
		<div>
			<Button
				className={classes.link}
				href={bookingLink}
				size={isDesktop ? 'medium' : 'small'}
				title={viewBookingLabel}
				data-cy="upcomingOfferSummary-button"
				asText
			>
				{viewBookingLabel}
			</Button>
		</div>
	);

	return (
		<OfferSummaryTemplate
			accommodation={accommodation}
			imageOverlay={<ImageOverlay />}
			topBlock={<TopBlock />}
			midBlock={<MidBlock />}
			bottomBlock={<BottomBlock />}
		/>
	);
};
